import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { sessionQueryOptions } from '@/lib/auth-query-options';
import { staffSearchServerFn, startImpersonationServerFn } from '@/server/functions/impersonation';

export const Route = createFileRoute('/$orgSlug/admin/impersonate')({
  ssr: false,
  beforeLoad: ({ context }) => {
    const session = context.queryClient.getQueryData<{
      user: { role?: string };
    }>(['auth-session']);
    if ((session?.user as { role?: string })?.role !== 'mesa_staff') {
      throw redirect({ to: '/' });
    }
  },
  component: ImpersonatePage,
});

type SearchResult = Awaited<ReturnType<typeof staffSearchServerFn>>;
type OrgResult = SearchResult['orgs'][number];
type MemberResult = OrgResult['members'][number];
type UserOrgResult = SearchResult['users'][number]['organizations'][number];

const PAGE_SIZE = 10;

function ImpersonatePage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const session = queryClient.getQueryData<{ user: { id: string } }>(sessionQueryOptions.queryKey);
  const currentUserId = session?.user?.id;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
      setExpandedOrgs(new Set());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: result = null, isLoading: loading } = useQuery({
    queryKey: ['staff-search', debouncedSearch, offset],
    queryFn: () =>
      staffSearchServerFn({
        data: { search: debouncedSearch || undefined, offset },
      }),
  });

  async function handleImpersonate(userId: string) {
    setImpersonating(userId);
    setError(null);
    try {
      await startImpersonationServerFn({ data: { targetUserId: userId } });
      queryClient.clear();
      window.location.assign('/');
    } catch (e) {
      setImpersonating(null);
      const message = e instanceof Error ? e.message : 'Impersonation failed';
      setError(message);
    }
  }

  function toggleOrg(orgSlug: string) {
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      if (next.has(orgSlug)) next.delete(orgSlug);
      else next.add(orgSlug);
      return next;
    });
  }

  function renderImpersonateButton(userId: string, label: string) {
    if (userId === currentUserId) {
      return <span className="text-xs text-mesa-muted">You</span>;
    }
    return (
      <Button
        size="sm"
        variant="destructive"
        disabled={impersonating !== null}
        onClick={() => handleImpersonate(userId)}
      >
        {impersonating === userId ? 'Starting...' : label}
      </Button>
    );
  }

  const hasResults = result && (result.orgs.length > 0 || result.users.length > 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Impersonate</h1>
        <p className="text-sm text-mesa-muted mt-1">Impersonate to View User Organization Dashboards</p>
      </div>

      <Input
        placeholder="Search by organization, name, or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      {error && (
        <div className="rounded border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400">{error}</div>
      )}

      {loading ? (
        <div className="text-sm text-mesa-muted py-8 text-center">Loading...</div>
      ) : !hasResults ? (
        <div className="text-sm text-mesa-muted py-8 text-center">No results found.</div>
      ) : (
        <>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[20%] pl-[calc(0.75rem+20px)]">Organization</TableHead>
                <TableHead className="w-[15%]">Org ID</TableHead>
                <TableHead className="w-[15%]">Member Name</TableHead>
                <TableHead className="w-[20%]">Member Email</TableHead>
                <TableHead className="w-[10%]">Member Role</TableHead>
                <TableHead className="w-[20%]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.orgs.flatMap((org: OrgResult) => {
                const owner = org.members[0];
                const isExpanded = expandedOrgs.has(org.orgSlug);
                const hasMore = org.members.length > 1;

                return [
                  <TableRow
                    key={org.orgSlug}
                    className={hasMore ? 'cursor-pointer' : undefined}
                    onClick={hasMore ? () => toggleOrg(org.orgSlug) : undefined}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-mesa-muted shrink-0 w-3">
                          {hasMore ? (isExpanded ? '▼' : '▶') : ''}
                        </span>
                        <div>
                          <div>{org.orgName}</div>
                          <div className="text-xs text-mesa-muted">{org.orgSlug}</div>
                          {hasMore && <div className="text-xs text-mesa-muted">{org.members.length} members</div>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs truncate">{org.orgId}</TableCell>
                    <TableCell>{owner?.userName}</TableCell>
                    <TableCell className="font-mono text-sm">{owner?.userEmail}</TableCell>
                    <TableCell>{owner?.role}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {owner && renderImpersonateButton(owner.userId, 'Impersonate Owner')}
                    </TableCell>
                  </TableRow>,
                  ...(isExpanded
                    ? org.members.slice(1).map((m: MemberResult) => (
                        <TableRow key={`${org.orgSlug}-${m.userId}`}>
                          <TableCell />
                          <TableCell />
                          <TableCell>{m.userName}</TableCell>
                          <TableCell className="font-mono text-sm">{m.userEmail}</TableCell>
                          <TableCell>{m.role}</TableCell>
                          <TableCell>{renderImpersonateButton(m.userId, 'Impersonate Member')}</TableCell>
                        </TableRow>
                      ))
                    : []),
                ];
              })}
              {result.users.flatMap((u) =>
                u.organizations.length > 0
                  ? u.organizations.map((org: UserOrgResult) => (
                      <TableRow key={`${u.userId}-${org.orgId}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="shrink-0 w-3" />
                            <div>
                              <div>{org.orgName}</div>
                              <div className="text-xs text-mesa-muted">{org.orgSlug}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs truncate">{org.orgId}</TableCell>
                        <TableCell>{u.userName}</TableCell>
                        <TableCell className="font-mono text-sm">{u.userEmail}</TableCell>
                        <TableCell>{org.role}</TableCell>
                        <TableCell>{renderImpersonateButton(u.userId, 'Impersonate')}</TableCell>
                      </TableRow>
                    ))
                  : [
                      <TableRow key={u.userId}>
                        <TableCell>
                          <span className="text-xs text-mesa-muted">—</span>
                        </TableCell>
                        <TableCell />
                        <TableCell>{u.userName}</TableCell>
                        <TableCell className="font-mono text-sm">{u.userEmail}</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell>{renderImpersonateButton(u.userId, 'Impersonate')}</TableCell>
                      </TableRow>,
                    ]
              )}
            </TableBody>
          </Table>

          {result.orgTotal > PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-mesa-muted">
                Page {Math.floor(offset / PAGE_SIZE) + 1} of {Math.ceil(result.orgTotal / PAGE_SIZE)} ({result.orgTotal}{' '}
                organizations)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + PAGE_SIZE >= result.orgTotal}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
